import React, { useEffect, useMemo, useState } from "react";
import { EditorDialog, EditorInput, Button } from "./ScheduleEditorUI";
import { useFormik } from "formik";
import { failed, success } from "../../common/Toastify";
import { createAvailabilityRule, updateAvailabilityRule } from "../../api/Scheduling/availabilityRules";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { useServiceCategoriesQuery } from "../../../hooks/ReactQueryHooks/useServiceCategoriesQuery";
import { timeZoneLabel } from "./timeZones";
import { MAX_AVAILABILITY_RULE_WINDOWS_PER_WEEK } from "./constants";
import {
  collapseWindowsToShifts,
  cycleWeekIndex,
  exclusiveEndToInclusiveDate,
  expandShiftsToWindows,
  previewRuleEvents,
} from "./time";
import { ruleConflictMessage } from "./ranges";
import SchedulingCalendar from "./calendar/SchedulingCalendar";
import WorkWeekEditor from "./WorkWeekEditor";
import EndDateChoice from "./EndDateChoice";
import moment from "moment";

const emptyWindow = (weekIndex, categoryIds) => ({
  weekIndex,
  dayOfWeek: "MONDAY",
  startTime: "09:00",
  endTime: "17:00",
  mode: "regular",
  serviceCategoryIds: categoryIds,
  breaks: [],
});

const blankValues = (categoryIds) => ({
  effectiveFrom: "",
  openEnded: true,
  effectiveTo: "",
  cycleWeeks: 1,
  cycleAnchorDate: "",
  weeklyWindows: [emptyWindow(0, categoryIds)],
});

const AvailabilityRuleForm = ({
  show,
  onHide,
  organizationRef,
  schedule,
  rule,
  existingRules = [],
  onSaved,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [previewRange, setPreviewRange] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [formError, setFormError] = useState("");
  const categoryIds = schedule?.serviceCategoryIds || [];
  const isEdit = !!rule?.availabilityRuleId;
  const { data: categories = [] } = useServiceCategoriesQuery({ scope: "descendants", practitionerRef: schedule?.practitionerRef,
    organizationRef,
    active: true,
    enabled: !!organizationRef && show,
  });
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => categoryIds.includes(category.serviceCategoryId))
        .map((category) => ({
          name: `${category.name} (${category.durationMinutes} min)`,
          value: category.serviceCategoryId,
          organizationRef: category.organizationRef, organizationName: category.organizationName || category.organizationRef,
        })),
    [categories, categoryIds]
  );

  const formik = useFormik({
    initialValues: blankValues(categoryIds),
    enableReinitialize: true,
    onSubmit: (values) => {
      setFormError("");
      if (!values.effectiveFrom) {
        setFormError("Choose a start date.");
        return;
      }
      if (!values.openEnded && (!values.effectiveTo || values.effectiveTo < values.effectiveFrom)) {
        setFormError(values.effectiveTo ? "End date must be on or after start date." : "Choose an end date.");
        return;
      }
      if (!values.weeklyWindows.length) {
        failed("Add at least one weekday.");
        return;
      }
      if (values.weeklyWindows.some((window) => !window.serviceCategoryIds?.length)) {
        setFormError("Choose at least one appointment type for every shift.");
        return;
      }
      const cycleWeeks = Number(values.cycleWeeks) || 1;
      if (cycleWeeks > 1 && values.weeklyWindows.some((window) => window.weekIndex === undefined || window.weekIndex === "")) {
        failed("Each day in a multi-week cycle needs a week.");
        return;
      }
      const expanded = expandShiftsToWindows(
        values.weeklyWindows.map((window) => ({
          ...window,
          weekIndex: cycleWeeks > 1 ? Number(window.weekIndex) : 0,
          mode: window.mode || "regular",
        }))
      );
      if (expanded.error) {
        setFormError(expanded.error);
        return;
      }
      const weeklyWindows = expanded.windows;
      if (!weeklyWindows.length) {
        setFormError("Add at least one period of working time.");
        return;
      }
      const maximumPeriods = MAX_AVAILABILITY_RULE_WINDOWS_PER_WEEK * cycleWeeks;
      if (weeklyWindows.length > maximumPeriods) {
        setFormError(`This ${cycleWeeks}-week cycle exceeds the limit of ${maximumPeriods} working periods after breaks.`);
        return;
      }
      const conflict = ruleConflictMessage({
        rules: existingRules,
        availabilityRuleId: rule?.availabilityRuleId,
        effectiveFrom: values.effectiveFrom,
        effectiveTo: values.openEnded ? null : values.effectiveTo,
      });
      if (conflict) {
        setFormError(conflict);
        return;
      }
      const body = {
        organizationRef,
        scheduleId: schedule.scheduleId,
        effectiveFrom: values.effectiveFrom,
        ...(values.openEnded ? {} : { effectiveTo: values.effectiveTo }),
        cycleWeeks,
        cycleAnchorDate: values.cycleAnchorDate || values.effectiveFrom,
        weeklyWindows,
      };
      setBtnLoading(true);
      const request = isEdit
        ? updateAvailabilityRule({
            ...body,
            availabilityRuleId: rule.availabilityRuleId,
            expectedVersion: rule.version,
          })
        : createAvailabilityRule(body);
      request
        .then(() => {
          success(isEdit ? "Work schedule updated" : "Work schedule created");
          onSaved?.();
          onHide();
        })
        .catch((error) => setFormError(schedulingErrorMessage(error)))
        .finally(() => setBtnLoading(false));
    },
  });

  useEffect(() => {
    if (!show) return;
    setPreviewRange(null);
    setFormError("");
    if (rule) {
      formik.setValues({
        effectiveFrom: rule.effectiveFrom,
        openEnded: !rule.effectiveTo,
        effectiveTo: rule.effectiveTo || "",
        cycleWeeks: rule.cycleWeeks || 1,
        cycleAnchorDate: rule.cycleAnchorDate || rule.effectiveFrom,
        weeklyWindows: collapseWindowsToShifts(
          (rule.weeklyWindows || []).map((window) => ({
            weekIndex: window.weekIndex || 0,
            dayOfWeek: window.dayOfWeek,
            startTime: window.startTime,
            endTime: window.endTime,
            mode: window.mode || "regular",
            serviceCategoryIds: window.serviceCategoryIds || categoryIds,
          }))
        ),
      });
    } else {
      formik.setValues(blankValues(categoryIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, rule]);

  const effectiveTo = formik.values.openEnded ? undefined : formik.values.effectiveTo || undefined;
  const conflictNotice = useMemo(
    () =>
      formik.values.effectiveFrom
        ? ruleConflictMessage({
            rules: existingRules,
            availabilityRuleId: rule?.availabilityRuleId,
            effectiveFrom: formik.values.effectiveFrom,
            effectiveTo: effectiveTo || null,
          })
        : null,
    [existingRules, rule?.availabilityRuleId, formik.values.effectiveFrom, effectiveTo]
  );

  const previewInitialDate =
    formik.values.effectiveFrom || moment.tz(schedule?.timezoneName || "UTC").format("YYYY-MM-DD");
  const previewCycleWeek =
    Number(formik.values.cycleWeeks) > 1
      ? cycleWeekIndex(
          formik.values.cycleAnchorDate || formik.values.effectiveFrom || previewInitialDate,
          previewRange
            ? moment.tz(previewRange.start, schedule?.timezoneName || "UTC").format("YYYY-MM-DD")
            : previewInitialDate,
          formik.values.cycleWeeks
        ) + 1
      : null;

  const preview = useMemo(() => {
    const expanded = expandShiftsToWindows(formik.values.weeklyWindows);
    return previewRuleEvents({
      weeklyWindows: expanded.error ? formik.values.weeklyWindows : expanded.windows,
      cycleWeeks: formik.values.cycleWeeks,
      cycleAnchorDate: formik.values.cycleAnchorDate || formik.values.effectiveFrom,
      weekStart: previewInitialDate,
      rangeStart: previewRange?.start,
      rangeEnd: previewRange?.end,
      effectiveFrom: formik.values.effectiveFrom || undefined,
      effectiveTo,
      timezoneName: schedule?.timezoneName,
    });
  }, [
    formik.values.weeklyWindows,
    formik.values.cycleWeeks,
    formik.values.cycleAnchorDate,
    formik.values.effectiveFrom,
    effectiveTo,
    previewInitialDate,
    previewRange,
    schedule?.timezoneName,
  ]);

  const setCycleWeeks = (value) => {
    const cycleWeeks = Number(value);
    if (formik.values.weeklyWindows.some((window) => Number(window.weekIndex || 0) >= cycleWeeks)) {
      setFormError("This would remove a week with working hours. Deselect its working days before shortening the cycle.");
      return;
    }
    setFormError("");
    formik.setFieldValue("cycleWeeks", cycleWeeks);
  };

  const scheduleEndDate = schedule?.endsAt
    ? exclusiveEndToInclusiveDate(schedule.endsAt, schedule.timezoneName || "UTC")
    : null;

  return (
    <EditorDialog open={show} onClose={onHide} wide busy={btnLoading}
      title={isEdit ? "Edit work schedule" : "Create a work schedule"}
      description="Set the hours that repeat on your calendar."
      actions={<><Button variant="secondary" onClick={onHide} disabled={btnLoading}>Cancel</Button>
        <Button type="submit" form="work-schedule-form" isLoading={btnLoading} disabled={!!conflictNotice}>{isEdit ? "Save changes" : "Create work schedule"}</Button></>}>
        <form id="work-schedule-form" className="common-form border-fields schedule-editor-form" onSubmit={formik.handleSubmit}>
          <section className="schedule-date-settings">
            <div className="schedule-section-title"><h3>Dates and repeat</h3><span className="schedule-timezone">Schedule time: {timeZoneLabel(schedule?.timezoneName)}</span></div>
            <div className="schedule-field-grid schedule-field-grid--dates">
              <label>Start date<EditorInput aria-label="Start date" type="date" required value={formik.values.effectiveFrom}
                onChange={(event) => {
                  formik.setFieldValue("effectiveFrom", event.target.value);
                  if (!formik.values.cycleAnchorDate) formik.setFieldValue("cycleAnchorDate", moment.utc(event.target.value).startOf("isoWeek").format("YYYY-MM-DD"));
                }} /></label>
              <label>Repeats<select value={formik.values.cycleWeeks} onChange={(event) => setCycleWeeks(event.target.value)}>
                {Array.from({ length: 8 }, (_, index) => <option key={index} value={index + 1}>{index === 0 ? "Every week" : `Every ${index + 1} weeks`}</option>)}
              </select></label>
              <EndDateChoice
                id="work-schedule-end"
                openEnded={formik.values.openEnded}
                value={formik.values.effectiveTo}
                min={formik.values.effectiveFrom}
                max={scheduleEndDate}
                openEndedLabel={scheduleEndDate ? "With schedule" : "No end date"}
                openEndedHelp={scheduleEndDate ? moment.utc(scheduleEndDate).format("MMM D, YYYY") : undefined}
                onChange={({ openEnded, value }) => {
                  formik.setValues((values) => ({ ...values, openEnded, effectiveTo: value }));
                }}
              />
            </div>
            {conflictNotice && <p className="schedule-conflict-notice" role="status">{conflictNotice}</p>}
            {Number(formik.values.cycleWeeks) > 1 && <div className="schedule-cycle-start">
              <label>Week 1 starts on<EditorInput aria-label="Week 1 starts on" type="date" required value={formik.values.cycleAnchorDate}
                aria-describedby="cycle-date-help" onChange={(event) => formik.setFieldValue("cycleAnchorDate", event.target.value)} /></label>
              <p id="cycle-date-help">Choose the date your first cycle week begins, usually a Monday. It can be before the start date.</p>
            </div>}
          </section>
          <WorkWeekEditor windows={formik.values.weeklyWindows} onChange={(windows) => formik.setFieldValue("weeklyWindows", windows)}
            cycleWeeks={formik.values.cycleWeeks} categoryIds={categoryIds} categoryOptions={categoryOptions} />
          <details className="schedule-preview" onToggle={(event) => setPreviewVisible(event.currentTarget.open)}>
            <summary>Preview on the calendar</summary>
            <p className="schedule-help">Monday–Sunday · {previewCycleWeek ? `Week ${previewCycleWeek} of ${formik.values.cycleWeeks}` : "Repeats every week"}</p>
            {previewVisible && <SchedulingCalendar key={`rule-preview-${show}-${previewInitialDate}`} events={preview} timeZone={schedule?.timezoneName || "UTC"}
              initialDate={previewInitialDate} extraViews="timeGridWeek"
              headerToolbar={{ left: "prev,next today", center: "title", right: "" }}
              onDatesSet={({ start, end }) => setPreviewRange({ start, end })} readOnly />}
          </details>
          {formError && <p className="schedule-error" role="alert">{formError}</p>}
        </form>
    </EditorDialog>
  );
};

export default AvailabilityRuleForm;
